import type { ReactNode } from 'react';

import {
  AvatarFallback,
  AvatarImage,
  Avatar as AvatarPrimitive,
  type AvatarProps as AvatarPrimitiveProps,
} from '../../primitives';
import { cn, cva, type VariantProps } from '../../shared';

export const avatarVariants = cva(
  'bg-muted text-muted-foreground relative flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
  {
    variants: {
      size: {
        sm: 'size-control-sm text-caption',
        md: 'size-control text-body',
        lg: 'size-control-lg text-body',
        xl: 'size-control-xl text-title',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

type AvatarVariants = VariantProps<typeof avatarVariants>;

export interface AvatarProps extends Omit<AvatarPrimitiveProps, 'children'> {
  size?: NonNullable<AvatarVariants['size']>;
  src?: string;
  /** Empty by default: beside the name it belongs to, alt text is read twice. */
  alt?: string;
  fallback: ReactNode;
  /** Non-zero, or a cached image flashes its initials on the way in. */
  fallbackDelayMs?: number;
}

/** The control scale, not an avatar scale of its own, so it follows density with them. */
export function Avatar({
  size,
  src,
  alt = '',
  fallback,
  fallbackDelayMs = 300,
  className,
  ...props
}: AvatarProps) {
  return (
    <AvatarPrimitive className={cn(avatarVariants({ size }), className)} {...props}>
      {src === undefined ? null : (
        <AvatarImage src={src} alt={alt} className="size-full object-cover" />
      )}
      <AvatarFallback delayMs={fallbackDelayMs} className="size-full grid place-items-center">
        {fallback}
      </AvatarFallback>
    </AvatarPrimitive>
  );
}
